import React, { useContext, useEffect, useState } from "react";
import "../../Tabels/Tabels.css";
import { Context } from "../../../utils/context";
import Select from "react-select";
import ModalSave from "../../common/ModelSave";
import { CancelButton, SaveButton } from "../../common/Button";
import { Row, Col, Form, Modal } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import classNames from "classnames";

// mode: "add" (manual stock-in) | "reduce" (manual stock-out)
const Adjust = ({ mode, show, handleClose }) => {
  const { postData, getData, Select2Data } = useContext(Context);
  const isAdd = mode === "add";

  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [currentStock, setCurrentStock] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showModal, setShowModal] = useState({ code: 0, message: "" });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const watchProduct = watch("product_id");
  const watchVariant = watch("variant_id");

  useEffect(() => {
    (async () => {
      const response = await getData("/common/masters/product");
      if (response?.success) {
        setProducts(await Select2Data(response?.data, "product_id"));
      }
    })();
  }, []);

  useEffect(() => {
    setValue("variant_id", null);
    setVariants([]);
    setCurrentStock(null);
    if (!watchProduct?.value) return;
    (async () => {
      const response = await getData(
        `/common/masters/product-varient/${watchProduct.value}`,
      );
      if (response?.success) {
        setVariants(
          response?.data?.map((data) => ({
            value: data?.id,
            label: `${data?.Metal?.name || ""} ${data?.Product?.Unit?.name || ""}`,
            stock: data?.general_stock,
            name: "variant_id",
          })),
        );
      }
    })();
  }, [watchProduct]);

  useEffect(() => {
    setCurrentStock(watchVariant ? (watchVariant.stock ?? 0) : null);
  }, [watchVariant]);

  const onSubmit = async (data) => {
    try {
      setSubmitLoading(true);
      const body = new FormData();
      body.append("variant_id", data.variant_id.value);
      body.append("quantity", data.quantity);
      body.append("remark", data.remark || "");
      if (isAdd) {
        body.append("batch_no", data.batch_no || "");
        body.append("price", data.price || "");
        body.append("mfg_date", data.mfg_date || "");
        body.append("expiry_date", data.expiry_date || "");
      } else {
        body.append("reason", data.reason);
      }

      const response = await postData(
        `/admin/purchase-order/stock-adjustment/${mode}`,
        body,
      );
      setShowModal({
        code: response?.code,
        message: response?.success ? response.message : response?.errors,
      });
      setTimeout(() => {
        setShowModal({ code: 0, message: "" });
        if (response?.success) handleClose();
        setSubmitLoading(false);
      }, 1000);
    } catch (error) {
      console.log(error);
      setSubmitLoading(false);
    }
  };

  const numberKeys = (e) => {
    if (["Backspace", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) return;
    if (!/[\d.]/.test(e.key)) e.preventDefault();
    if (e.key === "." && e.target.value.includes(".")) e.preventDefault();
  };

  const field = (label, name, rules, props = {}) => (
    <Col md={6} className="mt-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        className={classNames("", { "is-invalid": errors?.[name] })}
        {...register(name, rules)}
        {...props}
      />
      {errors?.[name] && (
        <span className="text-danger">{errors[name].message}</span>
      )}
    </Col>
  );

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{isAdd ? "Add Stock" : "Reduce Stock"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)} role="form">
            <Row>
              <Col md={6} className="mt-3">
                <Form.Label>Product</Form.Label>
                <Controller
                  name="product_id"
                  control={control}
                  rules={{ required: "Select Product" }}
                  render={({ field }) => (
                    <Select {...field} options={products} />
                  )}
                />
                {errors?.product_id && (
                  <span className="text-danger">
                    {errors.product_id.message}
                  </span>
                )}
              </Col>
              <Col md={6} className="mt-3">
                <Form.Label>Variant</Form.Label>
                <Controller
                  name="variant_id"
                  control={control}
                  rules={{ required: "Select Variant" }}
                  render={({ field }) => (
                    <Select {...field} options={variants} />
                  )}
                />
                {errors?.variant_id && (
                  <span className="text-danger">
                    {errors.variant_id.message}
                  </span>
                )}
                {currentStock !== null && (
                  <small className="text-muted">
                    Current stock: <b>{currentStock}</b>
                  </small>
                )}
              </Col>

              {field(
                "Quantity",
                "quantity",
                {
                  required: "Quantity is required",
                  validate: (v) =>
                    Number(v) > 0 || "Quantity must be greater than 0",
                },
                {
                  type: "text",
                  placeholder: "Quantity",
                  onKeyDown: numberKeys,
                },
              )}

              {isAdd ? (
                <>
                  {field(
                    "Price (per unit)",
                    "price",
                    {},
                    {
                      type: "text",
                      placeholder: "Price",
                      onKeyDown: numberKeys,
                    },
                  )}
                  {field(
                    "Batch No.",
                    "batch_no",
                    { required: "Batch No. is required" },
                    { type: "text", placeholder: "Batch No." },
                  )}
                  {field("Mfg Date", "mfg_date", {}, { type: "date" })}
                  {field(
                    "Expiry Date",
                    "expiry_date",
                    {
                      validate: (v) =>
                        !v ||
                        !watch("mfg_date") ||
                        v >= watch("mfg_date") ||
                        "Expiry date cannot be before Mfg date",
                    },
                    { type: "date" },
                  )}
                </>
              ) : (
                field(
                  "Reason",
                  "reason",
                  {
                    required: "Reason is required",
                    minLength: {
                      value: 3,
                      message: "Reason must be at least 3 characters",
                    },
                    maxLength: {
                      value: 100,
                      message: "Reason cannot exceed 100 characters",
                    },
                  },
                  { type: "text", placeholder: "e.g. Damaged, Expired, Lost" },
                )
              )}

              <Col md={12} className="mt-3">
                <Form.Label>Remark</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Remark"
                  className={classNames("", { "is-invalid": errors?.remark })}
                  {...register("remark", {
                    minLength: {
                      value: 3,
                      message: "Remark must be at least 3 characters",
                    },
                    maxLength: {
                      value: 250,
                      message: "Remark cannot exceed 250 characters",
                    },
                  })}
                />
                {errors?.remark && (
                  <span className="text-danger">{errors.remark.message}</span>
                )}
              </Col>
            </Row>

            <Row className="mt-4 pb-3">
              <div className="d-flex justify-content-center">
                <CancelButton name={"cancel"} handleClose={handleClose} />
                <SaveButton
                  name={"save"}
                  handleSubmit={handleSubmit(onSubmit)}
                  disabled={submitLoading}
                />
              </div>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>

      <ModalSave
        message={showModal.message}
        showErrorModal={showModal.code ? true : false}
      />
    </>
  );
};

export default Adjust;
